import { ArrowLeftOutlined, DragOutlined, FlagOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons'
import { useDriverSchedules } from '@api/useDriverSchedules'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { filterDriversByHomeLocation } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { AutoComplete, Badge, Button, Checkbox, Col, Dropdown, Input, message, Row, Select, Switch } from 'antd'
import classNames from 'classnames'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useSecurityContext } from '../../Navigation/SecurityContext'
import { SCHEDULE_EDITING_MODES, SHIFT_TYPES } from '../Schedule/util'
import { DriverSortMenu } from './DriverSortMenu'

const { Option, OptGroup } = Select

const store = window.localStorage

export const driverSortOptions = {
  'Driver Name': 'name',
  'Start Time': 'start_time',
}

export function DriverHeader({
  filters,
  title,
  drivers,
  setDrivers,
  loading,
  unfilteredDrivers,
  depots,
  carrierDepots,
  filteredDepots,
  setFilteredDepots,
  selectedDriver,
  setSelectedDriver,
  setShowActiveDrivers,
  showActiveDrivers,
  parameters,
  setParameters,
  bulkEditParameters,
  setBulkEditParameters,
  showActiveToggle,
  showDraggable = true,
  activeDrivers,
  inactiveDrivers,
  fullHeight,
  shiftTypeFilter,
  setShiftTypeFilter,
  editingMode,
  showSelectAllDrivers,
  setSearchParams,
}) {
  const { scopes } = useSecurityContext()
  const [filteredDrivers, setFilteredDrivers] = useState([])
  const canEditSchedule = scopes.driver.schedules.edit
  useEffect(() => setFilteredDrivers(unfilteredDrivers), [unfilteredDrivers])

  const handleDepotChange = (selectedDepots) => {
    if (selectedDepots.length === 0) {
      setDrivers(unfilteredDrivers)
    } else if (activeDrivers && activeDrivers.length > 0 && showActiveDrivers === true) {
      setDrivers(filterDriversByHomeLocation(activeDrivers, selectedDepots))
    } else if (inactiveDrivers && inactiveDrivers.length > 0 && showActiveDrivers === false) {
      setDrivers(filterDriversByHomeLocation(inactiveDrivers, selectedDepots))
    }
    setFilteredDepots(selectedDepots)
  }

  const onDriverSelect = (value) => {
    const match = drivers.find((x) => x.id === value)
    setSelectedDriver(match)
    setFilteredDrivers(drivers)
  }

  const onDriverSearch = (value) => {
    const v = value.toLowerCase()
    setFilteredDrivers(!value ? drivers : filteredDrivers.filter((x) => x.name.toLowerCase().includes(v)))
  }

  const handleToggleChange = () => {
    if (!filteredDepots) {
      setShowActiveDrivers(!showActiveDrivers)
    } else if (filteredDepots.length && showActiveDrivers === false) {
      setDrivers(filterDriversByHomeLocation(activeDrivers, filteredDepots))
    } else if (filteredDepots.length && showActiveDrivers === true) {
      setDrivers(filterDriversByHomeLocation(inactiveDrivers, filteredDepots))
    }
    setShowActiveDrivers(!showActiveDrivers)
  }

  const renderItem = (index, driver) => {
    const isCarrier = !!driver.carrier_id
    return {
      value: driver.id,
      label: (
        <div key={driver.id} className='driver-search-render-label'>
          <div className='bold-text'>
            <Horizontal
              style={{ gap: '0.25rem', color: isCarrier ? 'var(--theme-color-4)' : 'default' }}
              alignItems='center'
            >
              {isCarrier && <FlagOutlined />} {driver.name} - {driver.home_location}
            </Horizontal>
          </div>
        </div>
      ),
      index,
    }
  }

  const driverOptions = [
    {
      label: 'Drivers',
      options: filteredDrivers && filteredDrivers.filter((d) => d.is_active === true).map((d, i) => renderItem(i, d)),
    },
  ]

  return (
    <div
      className={classNames('driver-header-container vertical-flex-center', {
        'h-100': fullHeight && !selectedDriver,
      })}
    >
      {selectedDriver ? (
        <SelectedDriverHeaderSection
          driver={selectedDriver}
          carrierDepots={carrierDepots}
          drivers={unfilteredDrivers}
          driverOptions={driverOptions}
          onDriverSelect={onDriverSelect}
          onDriverSearch={onDriverSearch}
          setSelectedDriver={setSelectedDriver}
          setDrivers={setDrivers}
          setFilteredDrivers={setFilteredDrivers}
          showDraggable={showDraggable}
          canEditSchedule={canEditSchedule}
          setSearchParams={setSearchParams}
        />
      ) : (
        <>
          <DriverHeaderSection
            filters={filters}
            title={title}
            loading={loading}
            drivers={drivers}
            driverOptions={driverOptions}
            setDrivers={setDrivers}
            setFilteredDrivers={setFilteredDrivers}
            depots={depots}
            carrierDepots={carrierDepots}
            showActiveToggle={showActiveToggle}
            filteredDepots={filteredDepots}
            onDriverSelect={onDriverSelect}
            onDriverSearch={onDriverSearch}
            handleDepotChange={handleDepotChange}
            handleToggleChange={handleToggleChange}
            showActiveDrivers={showActiveDrivers}
            shiftTypeFilter={shiftTypeFilter}
            setShiftTypeFilter={setShiftTypeFilter}
          />
          {showSelectAllDrivers && (
            <SelectAllDrivers
              drivers={drivers}
              parameters={parameters}
              setParameters={setParameters}
              bulkEditParameters={bulkEditParameters}
              setBulkEditParameters={setBulkEditParameters}
              editingMode={editingMode}
            />
          )}
        </>
      )}
    </div>
  )
}

function DriverHeaderSection({
  filters,
  title,
  drivers,
  driverOptions,
  setDrivers,
  setFilteredDrivers,
  depots,
  carrierDepots,
  loading,
  filteredDepots,
  onDriverSelect,
  onDriverSearch,
  handleDepotChange,
  handleToggleChange,
  showActiveDrivers,
  showActiveToggle,
  shiftTypeFilter,
  setShiftTypeFilter,
}) {
  const [sortKey, setSortKey] = useState(driverSortOptions['Driver Name'])
  const handleSort = (sortKey) => {
    setSortKey(sortKey)
    if (sortKey === driverSortOptions['Start Time']) {
      setDrivers([
        ...drivers.sort((a, b) => {
          const aStart = moment(a?.[`${a.shift_preference}_shift`]?.start_time).format('HH:mm')
          const bStart = moment(b?.[`${b.shift_preference}_shift`]?.start_time).format('HH:mm')
          return aStart > bStart ? 1 : -1
        }),
      ])
    } else {
      setDrivers([...drivers.sort((a, b) => (a[sortKey] > b[sortKey] ? 1 : -1))])
    }
  }

  return (
    <Vertical border='all' justifyContent='flex-end' className='p-3 driver-header-section' height='auto'>
      <div className='all-drivers-header flex-1 mb-3'>{title}</div>

      <Vertical justifyContent='flex-end' style={{ gap: '0.5rem', overflow: 'visible', width: '100%' }}>
        <Horizontal alignItems='center' gap={4}>
          <DriverSearch
            filters={filters}
            drivers={driverOptions}
            setDrivers={setDrivers}
            setFilteredDrivers={setFilteredDrivers}
            carrierDepots={carrierDepots}
            onSelect={onDriverSelect}
            onSearch={onDriverSearch}
            loading={loading}
          />
          <DriverSortMenu
            handleSort={({ key }) => handleSort(key)}
            sortKey={sortKey}
            options={driverSortOptions}
            loading={loading}
          />
        </Horizontal>
        <DepotMultiSelect
          loading={loading}
          depots={depots}
          carrierDepots={carrierDepots}
          filteredDepots={filteredDepots}
          handleDepotChange={handleDepotChange}
        />

        {/** HACK - hide AM/PM filter on driver info. This is a shared component, but we'll refactor this garbage later anyways 👋🏼 */}
        {!window.location.pathname.includes('driver_info') && (
          <Button.Group size='small'>
            {Object.entries(SHIFT_TYPES).map(([key, value]) => (
              <GraviButton
                key={key}
                buttonText={key}
                theme2={shiftTypeFilter === value}
                onClick={() => setShiftTypeFilter(value)}
              />
            ))}
          </Button.Group>
        )}
      </Vertical>

      <div>
        {showActiveToggle && (
          <ActiveDriverToggle handleToggleChange={handleToggleChange} showActiveDrivers={showActiveDrivers} />
        )}
      </div>
    </Vertical>
  )
}

function SelectedDriverHeaderSection({
  driver,
  carrierDepots,
  driverOptions,
  onDriverSelect,
  onDriverSearch,
  setSelectedDriver,
  setDrivers,
  setFilteredDrivers,
  showDraggable,
  canEditSchedule,
  setSearchParams,
}) {
  let firstName = ''
  const fullName = driver.name && driver.name.trim()

  if (fullName && fullName.includes(' ')) {
    const names = driver.name.split(' ')
    firstName = names[0]
  } else {
    firstName = fullName
  }

  return (
    <>
      <div className='p-2 selected-driver-header-section' style={{ marginTop: '1.3rem' }}>
        <SelectedDriverHeaderSectionInfo
          driver={driver}
          location={driver.home_location}
          setSelectedDriver={setSelectedDriver}
          setSearchParams={setSearchParams}
        />
      </div>
      {driver.next_tractor_maintenance_required && (
        <MaintenanceSection
          firstName={firstName}
          date={formatters.DATE(moment(driver.next_tractor_maintenance))}
          showDraggable={showDraggable}
          canEditSchedule={canEditSchedule}
        />
      )}

      <div className='selected-driver-auto-complete'>
        <Vertical className='m-3'>
          <DriverSearch
            carrierDepots={carrierDepots}
            drivers={driverOptions}
            setDrivers={setDrivers}
            setFilteredDrivers={setFilteredDrivers}
            onSelect={onDriverSelect}
            onSearch={onDriverSearch}
          />
        </Vertical>
      </div>
    </>
  )
}

function ActiveDriverToggle({ handleToggleChange, showActiveDrivers }) {
  return (
    <Row flex={3} align='top' justify='space-between'>
      <Col>
        <Texto category='h5' appearance='medium'>
          Driver Status:
        </Texto>
      </Col>
      <Col>
        <Switch
          defaultChecked={showActiveDrivers}
          onChange={handleToggleChange}
          checkedChildren='Active'
          unCheckedChildren='Inactive'
        />
      </Col>
    </Row>
  )
}

function DriverSearch({
  filters,
  drivers,
  setDrivers,
  setFilteredDrivers,
  carrierDepots = [],
  onSelect,
  onSearch,
  loading,
}) {
  const { useAddResourceMutation } = useDriverSchedules()

  async function handleResourceAdd(e, depot) {
    e.stopPropagation()
    const newResource = await useAddResourceMutation.mutateAsync({
      depot,
      start_date: filters.start_date,
      end_date: filters.end_date,
    })
    if (newResource) {
      message.success('Carrier Resource Added')
      setDrivers((prev) => [...prev, newResource])
      setFilteredDrivers((prev) => [...prev, newResource])
    } else {
      message.error('Failed to Add Carrier Resource')
    }
  }

  return (
    <Horizontal justifyContent='space-between' alignItems='center' style={{ gap: '0.5rem', width: '100%' }}>
      <AutoComplete
        style={{ width: '100%' }}
        options={drivers}
        disabled={loading}
        onSelect={(value, option) => onSelect(value, option)}
        onSearch={(value, option) => onSearch(value, option)}
      >
        <Input.Search placeholder='Search For Driver' style={{ width: '100%' }} />
      </AutoComplete>
      {carrierDepots.length > 0 && (
        <Dropdown
          trigger={['click']}
          overlay={
            <Vertical
              style={{ overflow: 'visible' }}
              className='carrier-depot-dropdown-container bordered rounded mt-2'
            >
              {carrierDepots.map((cd) => (
                <div className='carrier-depot-dropdown-option' onClick={(e) => e.stopPropagation()}>
                  <CarrierDepotRender name={cd.name} carrier={cd.carrier} />
                  <GraviButton
                    icon={<PlusOutlined />}
                    theme2
                    size='small'
                    onClick={(e) => handleResourceAdd(e, cd.name)}
                  />
                </div>
              ))}
            </Vertical>
          }
        >
          <div>
            <Badge
              size='small'
              offset={[-4, 25]}
              count={
                <div
                  style={{
                    backgroundColor: 'var(--theme-color-2)',
                    borderRadius: '100%',
                    color: 'white',
                    border: '1px solid white',
                    fontSize: '0.7em',
                  }}
                >
                  <PlusOutlined />
                </div>
              }
            >
              <GraviButton
                icon={<FlagOutlined />}
                style={{ backgroundColor: 'var(--theme-color-4)', color: 'white' }}
              />
            </Badge>
          </div>
        </Dropdown>
      )}
    </Horizontal>
  )
}

export function CarrierDepotRender({ name, carrier }) {
  return (
    <Texto
      category='label'
      style={{ display: 'flex', alignItems: 'center', lineHeight: '0.25', gap: 8, color: 'var(--theme-color-4)' }}
    >
      <FlagOutlined />
      {name} - {carrier}
    </Texto>
  )
}

function DepotMultiSelect({ depots, filteredDepots, carrierDepots = [], handleDepotChange, loading }) {
  const options = depots.map((d, i) => {
    return (
      <Option key={i} value={d}>
        {d}
      </Option>
    )
  })

  if (carrierDepots && carrierDepots.length > 0) {
    options.push(
      <OptGroup label='Carrier Depots'>
        {carrierDepots.map((cd, i) => {
          return (
            <Option key={options.length + i} value={cd.name}>
              <CarrierDepotRender name={cd.name} carrier={cd.carrier} />
            </Option>
          )
        })}
      </OptGroup>
    )
  }

  if (filteredDepots && filteredDepots !== '')
    return (
      <Select
        mode='multiple'
        allowClear
        disabled={loading}
        style={{ overflowY: 'auto', width: '100%' }}
        value={filteredDepots || []}
        placeholder='Filter By Depots'
        onChange={handleDepotChange}
        className='mb-2'
      >
        {options}
      </Select>
    )

  return (
    <Select
      mode='multiple'
      allowClear
      style={{ overflowY: 'auto', width: '100%' }}
      placeholder='Filter By Depot'
      onChange={handleDepotChange}
      className='mb-2'
    >
      {options}
    </Select>
  )
}

function SelectedDriverHeaderSectionInfo({ driver, location, setSelectedDriver, setSearchParams }) {
  return (
    <div style={{ overflow: 'ellipsis' }}>
      <Row align='bottom'>
        <Col span={24}>
          <Row>
            <Col span={12} align='left' className='pl-2 pt-1'>
              Selected Driver
            </Col>
          </Row>
          <Row>
            <Col span={24} className='pl-1 pt-1 selected-driver-name'>
              {driver.name}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col className='pr-2 selected-driver-origin' span={24} align='right'>
          Home Location
        </Col>
      </Row>
      <Row>
        <Col className='pl-2 back-to-all-drivers' span={12} align='left'>
          <GraviButton
            onClick={() => {
              setSelectedDriver(null)
            }}
            size='small'
            icon={<ArrowLeftOutlined />}
            buttonText='Back To All'
          />
        </Col>
        <Col className='pr-2 selected-driver-home-location' span={12} align='right'>
          {location}
        </Col>
      </Row>
    </div>
  )
}

function MaintenanceSection({ firstName, date, showDraggable, canEditSchedule }) {
  const location = store.getItem('current_driver_page')
  const onSchedulesTab = location === 'schedule'
  return (
    <div className='driver-maintenance-container p-3'>
      <Row>
        <Col span={24} align='center'>
          <WarningFilled /> {firstName}'s truck needs maintenance by {date}.
        </Col>
      </Row>
      {showDraggable && canEditSchedule && onSchedulesTab && (
        <Row>
          <Col span={24} align='center'>
            <div id='driver-maintenance-schedule-drag' className='driver-maintenance-schedule-drag'>
              <DragOutlined />
              <span className='pl-1'>Drag onto Schedule</span>
            </div>
          </Col>
        </Row>
      )}
    </div>
  )
}
function SelectAllDrivers({
  drivers,
  parameters,
  setParameters,
  bulkEditParameters,
  setBulkEditParameters,
  editingMode,
}) {
  const onSelectAllChange = (e) => {
    const copy = editingMode === SCHEDULE_EDITING_MODES.BULK ? { ...bulkEditParameters } : { ...parameters }

    const carriers = drivers
      .filter((d) => d.carrier_id)
      .map((d) => ({ id: d.carrier_id, resource_num: d.carrier_resource_num, depot: d.home_location }))

    if (e.target.checked) {
      copy.driver_ids = drivers.map((d) => d.id)
      copy.carriers = carriers
    } else {
      copy.driver_ids = []
      copy.carriers = []
    }

    if (editingMode === SCHEDULE_EDITING_MODES.BULK) {
      setBulkEditParameters(copy)
    } else {
      setParameters(copy)
    }
  }
  return (
    <Horizontal className='p-3' bg='layout' height='auto' border='all'>
      <Checkbox
        onChange={onSelectAllChange}
        checked={
          editingMode === SCHEDULE_EDITING_MODES.BULK
            ? bulkEditParameters.driver_ids.length === drivers.length
            : parameters.driver_ids.length === drivers.length
        }
      >
        Select All Drivers for {editingMode === SCHEDULE_EDITING_MODES.BULK ? 'Bulk Edit' : 'Template'}
      </Checkbox>
    </Horizontal>
  )
}
